import { useState } from 'react';
import type { BoardCardItem } from '../../doc/schema';
import { thumbnailUrl } from '../../app/base';
import { useAssetUrl } from '../assets/localAssets';
import { pickVariant } from '../assets/variants';
import { useBoard, usePane } from '../context';
import { useBoardLookup } from './boardSummary';

/**
 * Board card: a link to another board, drawn like a dashboard tile. The cover is a custom image
 * (asset on this board) or the target board's live dashboard preview; the title falls back to
 * the board's own. Double-click opens the board (handled by the viewport).
 */
export function BoardCardBody({ item }: { item: BoardCardItem }) {
  const board = useBoard();
  const p = item.props;
  const lookup = useBoardLookup(p.boardId);
  const self = p.boardId === board.boardId;
  const title = p.title || lookup.board?.title || (lookup.status === 'missing' ? 'Board unavailable' : lookup.status === 'loading' ? '…' : 'Board');
  const preview = !p.coverAssetId && p.showPreview && lookup.board?.thumbnailAt ? thumbnailUrl(p.boardId, lookup.board.thumbnailAt) : null;
  const status = self ? 'this board' : lookup.status === 'missing' ? 'no access' : lookup.status === 'error' ? 'offline' : lookup.board && lookup.board.role !== 'owner' ? lookup.board.role : null;

  return (
    <div
      className={`bc bc-${p.layout}${p.coverAssetId || preview ? ' bc-has-cover' : ''}`}
      style={p.color ? ({ '--bc-color': p.color } as React.CSSProperties) : undefined}
    >
      <div className="bc-cover" data-cover>
        {p.coverAssetId ? (
          <AssetCover assetId={p.coverAssetId} fit={p.fit} width={item.w} height={item.h} />
        ) : preview ? (
          <PreviewCover url={preview} fit={p.fit} />
        ) : (
          <div className="bc-cover-empty" aria-hidden>
            <span className="bc-glyph">{(title.trim().charAt(0) || 'B').toUpperCase()}</span>
          </div>
        )}
      </div>
      <div className="bc-body">
        <div className="bc-kicker">
          Board
          {status && <span className="bc-status"> · {status}</span>}
        </div>
        <div className="bc-title">{title}</div>
        {p.description && <div className="bc-desc">{p.description}</div>}
      </div>
    </div>
  );
}

/** Custom cover from the asset store; picks the variant that suits the on-screen size. */
function AssetCover({ assetId, fit, width, height }: { assetId: string; fit: 'cover' | 'contain'; width: number; height: number }) {
  const zoom = usePane((s) => s.settledZoom);
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const url = useAssetUrl(assetId, pickVariant(Math.max(width, height) * zoom * dpr));
  if (!url) return <div className="bc-cover-empty image-placeholder" />;
  return <img className="bc-img" src={url} alt="" draggable={false} decoding="async" style={{ objectFit: fit }} />;
}

/** The target board's dashboard preview (served with the viewer's access rights). */
function PreviewCover({ url, fit }: { url: string; fit: 'cover' | 'contain' }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (failed === url) return <div className="bc-cover-empty" />;
  return <img className="bc-img" src={url} alt="" draggable={false} decoding="async" loading="lazy" style={{ objectFit: fit }} onError={() => setFailed(url)} />;
}
